<template>
  <section>
    <!-- New Recording Confirmation Modal -->
    <NewRecordingConfirmModal
      v-model:visible="showNewRecordingModal"
      @confirm="handleNewRecordingConfirm"
      @cancel="handleNewRecordingCancel"
    />

    <div class="border border-[#e0e0e0] rounded-[5px] overflow-visible shadow-sm relative">
      <!-- Loading Overlay -->
      <div
        v-if="loading"
        class="loading-overlay"
      >
        <div class="loading-content">
          <LoadingSpinner />
          <h3 class="loading-title">Generating your note....</h3>
          <p class="loading-subtitle">Please don't leave this page until the note is generated</p>
        </div>
      </div>

      <!-- Toolbar bar - always shown, but content only in draft mode -->
      <div class="bg-[#f2f2f2] flex items-center justify-between flex-wrap-reverse gap-2 px-4 py-2 min-h-[48px]">
        <template v-if="isDraft">
          <RichEditorToolbar
            :editor="editor"
            :selected-color="selectedColor"
            :selected-highlight="selectedHighlight"
            :selected-highlight-display="selectedHighlightDisplay"
            :show-highlight-panel="showHighlightPanel"
            :highlight-palette="highlightPalette"
            :border-color-icon="borderColorIcon"
            :format-bold-icon="formatBoldIcon"
            :format-italic-icon="formatItalicIcon"
            :format-underlined-icon="formatUnderlinedIcon"
            :format-list-bulleted-icon="formatListBulletedIcon"
            :format-list-numbered-icon="formatListNumberedIcon"
            @open-color-picker="openColorPicker"
            @color-input="handleColorInput"
            @toggle-highlight-panel="toggleHighlightPanel"
            @highlight-none="handleHighlightNone"
            @highlight-select="handleHighlightSelect"
            @highlight-input="handleHighlightInput"
          />

          <div class="flex items-center gap-4 ml-auto">
            <slot name="actions" :record="emitRecord">
              <!-- Default content if no slot provided -->
            </slot>
          </div>
        </template>
        <!-- Empty toolbar when signed (read-only) -->
      </div>

      <div class="bg-white min-h-[532px] relative cursor-text" @click="handleEditorShellClick">
        <!-- Save status - only shown when editing (draft mode) -->
        <div v-if="isDraft" class="absolute right-4 top-2 flex items-center gap-2 pointer-events-none">
          <span>
            <img
              v-if="saveStatus === 'saved'"
              src="@/assets/encounterNotes/check.svg"
              alt="save-status-icon"
              class="w-4 h-4"
            />
            <img
              v-if="saveStatus === 'error'"
              src="@/assets/encounterNotes/clear.svg"
              alt="save-status-icon"
              class="w-4 h-4"
            />
          </span>
          <span class="text-[14px] select-none text-[#C4C4C4]">
            {{ statusLabel }}
          </span>
        </div>
        <div class="editor-shell">
          <editor-content
            :editor="editor"
            class="editor-input"
          />
          <div
            v-if="isEditorEmpty"
            class="editor-placeholder"
          >
            {{ placeholder }}
          </div>

        </div>
      </div>
    </div>

    <!-- Slash Command Dropdown - Teleported to body for proper z-index -->
    <Teleport to="body">
      <SlashCommandMenu
        v-if="showSlashMenu && filteredSlashCommands.length > 0"
        :items="filteredSlashCommands"
        :selected-index="selectedSlashIndex"
        :position="slashMenuPosition"
        @select="executeSlashCommand"
        @hover="selectedSlashIndex = $event"
      />
    </Teleport>

    <!-- Patient Variable Dropdown - Teleported to body for proper z-index -->
    <Teleport to="body">
      <SlashCommandMenu
        v-if="showVariableMenu && filteredVariables.length > 0"
        :items="filteredVariables"
        :selected-index="selectedVariableIndex"
        :position="variableMenuPosition"
        @select="executeVariableCommand"
        @hover="selectedVariableIndex = $event"
      />
    </Teleport>

    <!-- Auto-match Dropdown (3-letter debounced) - Teleported to body for proper z-index -->
    <Teleport to="body">
      <SlashCommandMenu
        v-if="showAutoMatchMenu && autoMatchItems.length > 0"
        :items="autoMatchItems"
        :selected-index="selectedAutoMatchIndex"
        :position="autoMatchMenuPosition"
        @select="executeAutoMatch"
        @hover="selectedAutoMatchIndex = $event"
      />
    </Teleport>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch, nextTick } from "vue";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";

// Composables
import { useEditorMenus } from "@/views/encounter-notes/composables/useEditorMenus";
import { useEditorHighlight, HighlightMark } from "@/views/encounter-notes/composables/useEditorHighlight";
import { useEditorSave } from "@/views/encounter-notes/composables/useEditorSave";
import { useEditorCardActions } from "@/views/encounter-notes/composables/useEditorCardActions";

// Extensions
import CardActionNode from "./rich-editor/CardActionNode";

// Components
import LoadingSpinner from "./LoadingSpinner.vue";
import RichEditorToolbar from "./rich-editor/RichEditorToolbar.vue";
import SlashCommandMenu from "./rich-editor/SlashCommandMenu.vue";
import NewRecordingConfirmModal from "./NewRecordingConfirmModal.vue";

// Icons
import borderColorIcon from "@/assets/encounterNotes/border_color.svg";
import formatBoldIcon from "@/assets/encounterNotes/format_bold.svg";
import formatItalicIcon from "@/assets/encounterNotes/format_italic.svg";
import formatListBulletedIcon from "@/assets/encounterNotes/format_list_bulleted.svg";
import formatListNumberedIcon from "@/assets/encounterNotes/format_list_numbered.svg";
import formatUnderlinedIcon from "@/assets/encounterNotes/format_underlined.svg";

// ============================================================================
// Props & Emits
// ============================================================================

const props = defineProps({
  placeholder: { type: String, default: "Type here to reply ..." },
  modelValue: { type: String, default: "" },
  save: { type: Function, default: null },
  loading: { type: Boolean, default: false },
  saveStatus: { type: String, default: "" },
  isDraft: { type: Boolean, default: true },
  hasTranscription: { type: Boolean, default: false },
  patientVariables: { type: Array, default: () => [] },
  patientMatchData: { type: Object, default: null },
});

const emit = defineEmits([
  "record", "update:modelValue", "save", "slashCommand", "newRecording",
  "editTask", "deleteTask", "editAppointment", "cancelAppointment",
  "previewDocument", "deleteDocument", "seeOrderDetails", "seeFormDetails",
]);

// ============================================================================
// Recording modal (simple, stays inline)
// ============================================================================

const showNewRecordingModal = ref(false);
const handleNewRecordingConfirm = () => { showNewRecordingModal.value = false; emit("newRecording"); };
const handleNewRecordingCancel = () => { showNewRecordingModal.value = false; };
const emitRecord = () => emit("record");

const handleEditorShellClick = (e) => {
  if (!editor.value || !props.isDraft) return;
  // If clicking on a card action button, don't interfere
  if (e.target.closest('.card-action-btn')) return;
  // If the editor isn't focused, place cursor at end
  if (!editor.value.isFocused) {
    e.preventDefault();
    editor.value.commands.focus('end');
  }
};

// ============================================================================
// Editor ref (shared by all composables)
// ============================================================================

// Declared as ref first, assigned by useEditor below
const editor = ref(null);

// ============================================================================
// Save composable
// ============================================================================

const {
  localValue,
  lastSavedValue,
  statusLabel,
  runSave,
  debouncedSave,
  scheduleSave,
  handleBlur,
  handleFocus,
  stripActionButtonsFromHTML,
  initValue,
} = useEditorSave({
  props,
  emit,
  isAnyMenuOpen: () => menus.isAnyMenuOpen(),
});

initValue(props.modelValue);

// ============================================================================
// Card actions composable
// ============================================================================

const {
  insertSlashCommandResult,
  insertCardAction,
  updateCardAction,
  removeCardAction,
  markCardAsCancelled,
} = useEditorCardActions({ editor, props, emit, runSave });

// ============================================================================
// Menus composable
// ============================================================================

const menus = useEditorMenus({
  editor,
  props,
  emit,
  scheduleSave,
  insertSlashCommandResult,
});

const {
  showSlashMenu,
  slashMenuPosition,
  selectedSlashIndex,
  filteredSlashCommands,
  executeSlashCommand,
  showVariableMenu,
  variableMenuPosition,
  selectedVariableIndex,
  filteredVariables,
  executeVariableCommand,
  showAutoMatchMenu,
  autoMatchMenuPosition,
  selectedAutoMatchIndex,
  autoMatchItems,
  executeAutoMatch,
  onEditorUpdate,
  onSelectionUpdate,
  debouncedAutoMatch,
  MenuExtension,
} = menus;

// ============================================================================
// Highlight composable
// ============================================================================

const {
  selectedColor,
  selectedHighlight,
  selectedHighlightDisplay,
  showHighlightPanel,
  highlightPalette,
  updateColorState,
  openColorPicker,
  toggleHighlightPanel,
  handleColorInput,
  handleHighlightInput,
  handleHighlightSelect,
  handleHighlightNone,
} = useEditorHighlight({ editor });

// ============================================================================
// Editor setup
// ============================================================================

const editorInstance = useEditor({
  content: localValue.value,
  editable: props.isDraft,
  extensions: [
    StarterKit,
    TextStyle.configure({ mergeNested: true }),
    Color.configure({ types: ["textStyle"] }),
    HighlightMark,
    MenuExtension,
    CardActionNode.configure({ isDraft: props.isDraft, getIsDraft: () => props.isDraft }),
  ],
  onCreate: ({ editor: ed }) => {
    updateColorState(ed);
  },
  onUpdate: ({ editor: ed }) => {
    const html = ed.getHTML();
    localValue.value = html;
    emit("update:modelValue", html);
    updateColorState(ed);
    scheduleSave();
    onEditorUpdate(ed);
  },
  onSelectionUpdate: ({ editor: ed }) => {
    updateColorState(ed);
    onSelectionUpdate(ed);
  },
  onFocus: () => {
    handleFocus();
  },
  onBlur: () => {
    handleBlur();
    setTimeout(() => {
      menus.closeAllMenus();
    }, 200);
  },
});

// Assign the editor ref so composables can use it
editor.value = editorInstance.value;
watch(editorInstance, (val) => { editor.value = val; });

// ============================================================================
// Computed
// ============================================================================

const isEditorEmpty = computed(() => {
  const instance = editor.value;
  if (!instance) return true;
  return instance.isEmpty && instance.getText().trim().length === 0;
});

// ============================================================================
// Watchers
// ============================================================================

watch(
  () => props.modelValue,
  (next) => {
    const incoming = stripActionButtonsFromHTML(next) || "";
    if (incoming !== localValue.value) {
      debouncedSave.cancel();
      localValue.value = incoming;
      lastSavedValue.value = incoming;
      if (editor.value) editor.value.commands.setContent(incoming, false);
    }
  }
);

watch(
  () => props.isDraft,
  async (isDraft, oldIsDraft) => {
    if (editor.value) {
      editor.value.setEditable(isDraft);

      if (!isDraft && oldIsDraft !== undefined) {
        await nextTick();
        const currentHTML = editor.value.getHTML();
        const cleanedHTML = stripActionButtonsFromHTML(currentHTML);
        editor.value.commands.clearContent();
        await nextTick();
        editor.value.commands.setContent(cleanedHTML, false);
        localValue.value = cleanedHTML;
      }

      // When isDraft transitions back to true (e.g. after edit-lock acquired),
      // re-set content to trigger addNodeView with isDraft=true, restoring action buttons
      if (isDraft && oldIsDraft === false) {
        await nextTick();
        const currentHTML = editor.value.getHTML();
        editor.value.commands.clearContent();
        await nextTick();
        editor.value.commands.setContent(currentHTML, false);
      }
    }
  }
);

// ============================================================================
// Cleanup
// ============================================================================

onBeforeUnmount(() => {
  debouncedSave.cancel();
  debouncedAutoMatch.cancel();
  if (editor.value) editor.value.destroy();
});

// ============================================================================
// Expose
// ============================================================================

defineExpose({
  insertContent: insertSlashCommandResult,
  insertCardAction,
  updateCardAction,
  removeCardAction,
  markCardAsCancelled,
  focus: () => editor.value?.commands.focus(),
});
</script>

<style scoped>
section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

section > div:first-child {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 400px;
  box-sizing: border-box;
}

.editor-shell {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: visible;
}

.editor-input {
  padding: 1.5rem 1.25rem 1.25rem 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
  cursor: text;
}

.editor-input :deep(.ProseMirror) {
  outline: none;
  min-height: 100%;
  height: 100%;
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
  cursor: text;
}

.editor-input :deep(.ProseMirror .ProseMirror-gapcursor) {
  position: relative;
  min-height: 1.5em;
  padding: 4px 0;
}

.editor-input :deep(.ProseMirror .ProseMirror-gapcursor::after) {
  display: block;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 1px;
  height: 1.2em;
  border-top: none;
  border-left: 1px solid #333;
  animation: blink-caret 1s step-end infinite;
}

@keyframes blink-caret {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.editor-input :deep(.ProseMirror p) {
  margin: 0 0 0.5rem;
}

.editor-input :deep(.ProseMirror ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0 0 0.5rem;
}

.editor-input :deep(.ProseMirror ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 0 0 0.5rem;
}

.editor-placeholder {
  position: absolute;
  top: 1.5rem;
  left: 1.25rem;
  color: #9ca3af;
  pointer-events: none;
}

/* Card Action Icon styles - uses CSS to add SVG icon when loaded from API */
.editor-input :deep(.card-action-icon) {
  background-repeat: no-repeat;
  background-position: center;
  background-size: 32px 32px;
}

.editor-input :deep(.card-action-icon-task) {
  background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 6V12L16 14' stroke='%23004879' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle cx='12' cy='12' r='9' stroke='%23004879' stroke-width='2'/%3E%3C/svg%3E");
}

.editor-input :deep(.card-action-icon-appointment) {
  background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' stroke='%23004879' stroke-width='2'/%3E%3Cline x1='3' y1='9' x2='21' y2='9' stroke='%23004879' stroke-width='2'/%3E%3Cline x1='9' y1='2' x2='9' y2='6' stroke='%23004879' stroke-width='2' stroke-linecap='round'/%3E%3Cline x1='15' y1='2' x2='15' y2='6' stroke='%23004879' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
}

.editor-input :deep(.card-action-icon-order) {
  background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.5 9.4L7.5 4.21M21 16V8C21 7.46957 20.7893 6.96086 20.4142 6.58579C20.0391 6.21071 19.5304 6 19 6H5C4.46957 6 3.96086 6.21071 3.58579 6.58579C3.21071 6.96086 3 7.46957 3 8V16C3 16.5304 3.21071 17.0391 3.58579 17.4142C3.96086 17.7893 4.46957 18 5 18H19C19.5304 18 20.0391 17.7893 20.4142 17.4142C20.7893 17.0391 21 16.5304 21 16Z' stroke='%23004879' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpolyline points='3.27,6.96 12,12.01 20.73,6.96' stroke='%23004879' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cline x1='12' y1='12' x2='12' y2='22' stroke='%23004879' stroke-width='2'/%3E%3C/svg%3E");
}

.editor-input :deep(.card-action-icon-document) {
  background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z' stroke='%23004879' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpolyline points='14,2 14,8 20,8' stroke='%23004879' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* Card Action Button styles */
.editor-input :deep(.card-action-btn) {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
}

.editor-input :deep(.card-action-btn:hover) {
  opacity: 0.8;
}

.editor-input :deep(.card-action-btn) svg {
  flex-shrink: 0;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  min-height: 300px;
  border-radius: 5px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.loading-content :deep(.spinner-container) {
  height: auto;
  background-color: transparent;
}

.loading-title {
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  margin-top: 60px;
}

.loading-subtitle {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* New Recording Button */
.new-recording-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #81B0E1;
  border-radius: 4px;
  background: white;
  color: #81B0E1;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.new-recording-btn:hover {
  background: #F0F7FF;
  border-color: #5587BC;
  color: #5587BC;
}

.new-recording-btn :deep(svg path) {
  fill: currentColor;
}
</style>
